import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, MapPin, IndianRupee, Calendar, User, Users, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const ProjectApprovalModal = ({ open, onClose, project, onSuccess }) => {
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  
  const [formData, setFormData] = useState({
    contract_value: '',
    start_date: '',
    expected_completion_date: '',
    design_engineers: [],
    construction_engineers: [],
    optional_activities: [],
    admin_notes: ''
  });

  useEffect(() => {
    if (open) {
      fetchEngineers();
      // Pre-fill dates if available from project
      if (project) {
        setFormData(prev => ({
          ...prev,
          start_date: project.start_date || project.planned_start_date || '',
          expected_completion_date: project.expected_completion_date || ''
        }));
      }
    }
  }, [open, project]);

  const fetchEngineers = async () => {
    try {
      const res = await api.get('/users?role=engineer');
      setEngineers(res.data);
    } catch (error) {
      console.error('Failed to fetch engineers:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleEngineer = (engineerId, type) => {
    const field = type === 'design' ? 'design_engineers' : 'construction_engineers';
    const current = formData[field];
    if (current.includes(engineerId)) {
      handleChange(field, current.filter(id => id !== engineerId));
    } else {
      handleChange(field, [...current, engineerId]);
    }
  };

  const toggleOptionalActivity = (activity) => {
    const current = formData.optional_activities;
    if (current.includes(activity)) {
      handleChange('optional_activities', current.filter(a => a !== activity));
    } else {
      handleChange('optional_activities', [...current, activity]);
    }
  };

  const handleApprove = async () => {
    if (!formData.contract_value || !formData.start_date || !formData.expected_completion_date) {
      toast.error('Please fill in contract value, start date, and completion date');
      return;
    }

    const allEngineers = [...new Set([...formData.design_engineers, ...formData.construction_engineers])];
    if (allEngineers.length === 0) {
      toast.error('Please assign at least one engineer');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/projects/${project.project_id}/admin-approve`, {
        contract_value: parseFloat(formData.contract_value),
        start_date: formData.start_date,
        expected_completion_date: formData.expected_completion_date,
        assigned_engineers: allEngineers,
        design_engineers: formData.design_engineers,
        construction_engineers: formData.construction_engineers,
        optional_activities: formData.optional_activities,
        admin_notes: formData.admin_notes
      });
      toast.success('Project approved successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve project');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setRejecting(true);
    try {
      await api.post(`/projects/${project.project_id}/admin-reject`, {
        reason: rejectReason
      });
      toast.success('Project rejected');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject project');
    } finally {
      setRejecting(false);
    }
  };

  const optionalActivities = [
    { key: 'swimming_pool', label: 'Swimming Pool' },
    { key: 'interiors', label: 'Interior Design' },
    { key: 'landscaping', label: 'Landscaping' },
    { key: 'compound_wall', label: 'Compound Wall & Gate' }
  ];

  const designEngineers = engineers.filter(e => e.engineer_type === 'design');
  const constructionEngineers = engineers.filter(e => e.engineer_type === 'construction');

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white border-0 shadow-2xl rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold text-stone-900">
              Review Project Request
            </DialogTitle>
            <Badge className={project.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
              {project.status === 'pending_approval' ? 'Customer Request' : 'Engineer Submission'}
            </Badge>
          </div>
          <DialogDescription className="text-stone-500">
            Review and approve this project request
          </DialogDescription>
        </DialogHeader>

        {/* Project Details Summary */}
        <div className="bg-stone-50 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-stone-900 flex items-center">
            <Building2 className="h-4 w-4 mr-2 text-[#1e3a5f]" />
            {project.project_name}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-start">
              <MapPin className="h-4 w-4 mr-2 text-stone-400 mt-0.5" />
              <span className="text-stone-600">{project.location}</span>
            </div>
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2 text-stone-400" />
              <span className="text-stone-600">{project.customer_name}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t border-stone-200">
            <div>
              <span className="text-stone-500">Plot Size:</span>
              <span className="ml-2 font-medium">{project.plot_size || 'N/A'} sq.ft</span>
            </div>
            <div>
              <span className="text-stone-500">Built-up:</span>
              <span className="ml-2 font-medium">{project.built_up_area} sq.ft</span>
            </div>
            <div>
              <span className="text-stone-500">Floors:</span>
              <span className="ml-2 font-medium">{project.floors_planned || 'N/A'}</span>
            </div>
          </div>
          {project.specific_requirements && (
            <div className="pt-2 border-t border-stone-200">
              <span className="text-stone-500 text-sm">Requirements:</span>
              <p className="text-sm text-stone-700 mt-1">{project.specific_requirements}</p>
            </div>
          )}
          {project.customer_notes && (
            <div className="pt-2 border-t border-stone-200">
              <span className="text-stone-500 text-sm">Customer Notes:</span>
              <p className="text-sm text-stone-700 mt-1">{project.customer_notes}</p>
            </div>
          )}
        </div>

        {showRejectForm ? (
          <div className="space-y-4 pt-4">
            <div className="flex items-center text-amber-600 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Please provide a reason for rejecting this project</span>
            </div>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="min-h-[100px] border-stone-200 rounded-lg"
            />
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={rejecting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 pt-2">
            {/* Contract Value */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <IndianRupee className="h-4 w-4 mr-2 text-stone-400" />
                Contract Value (₹) *
              </Label>
              <Input
                type="number"
                value={formData.contract_value}
                onChange={(e) => handleChange('contract_value', e.target.value)}
                placeholder="e.g., 5000000"
                className="h-11 border-stone-200 rounded-lg"
                data-testid="contract-value-input"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-stone-400" />
                  Start Date *
                </Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  className="h-11 border-stone-200 rounded-lg"
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700">
                  Expected Completion *
                </Label>
                <Input
                  type="date"
                  value={formData.expected_completion_date}
                  onChange={(e) => handleChange('expected_completion_date', e.target.value)}
                  className="h-11 border-stone-200 rounded-lg"
                  data-testid="completion-date-input"
                />
              </div>
            </div>

            {/* Engineer Assignment */}
            <div className="space-y-4">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <Users className="h-4 w-4 mr-2 text-stone-400" />
                Assign Engineers *
              </Label>
              
              {/* Design Engineers */}
              {designEngineers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Design Engineers</span>
                  <div className="grid grid-cols-2 gap-2">
                    {designEngineers.map(eng => (
                      <div
                        key={eng.user_id}
                        onClick={() => toggleEngineer(eng.user_id, 'design')}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.design_engineers.includes(eng.user_id)
                            ? 'border-[#1e3a5f] bg-[#1e3a5f]/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-stone-900">{eng.name}</span>
                          {formData.design_engineers.includes(eng.user_id) && (
                            <Check className="h-4 w-4 text-[#1e3a5f]" />
                          )}
                        </div>
                        <span className="text-xs text-stone-500">{eng.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Construction Engineers */}
              {constructionEngineers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Construction Engineers</span>
                  <div className="grid grid-cols-2 gap-2">
                    {constructionEngineers.map(eng => (
                      <div
                        key={eng.user_id}
                        onClick={() => toggleEngineer(eng.user_id, 'construction')}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.construction_engineers.includes(eng.user_id)
                            ? 'border-[#1e3a5f] bg-[#1e3a5f]/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-stone-900">{eng.name}</span>
                          {formData.construction_engineers.includes(eng.user_id) && (
                            <Check className="h-4 w-4 text-[#1e3a5f]" />
                          )}
                        </div>
                        <span className="text-xs text-stone-500">{eng.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fallback for engineers without type */}
              {engineers.filter(e => !e.engineer_type).length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Other Engineers</span>
                  <div className="grid grid-cols-2 gap-2">
                    {engineers.filter(e => !e.engineer_type).map(eng => (
                      <div
                        key={eng.user_id}
                        onClick={() => toggleEngineer(eng.user_id, 'construction')}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.construction_engineers.includes(eng.user_id)
                            ? 'border-[#1e3a5f] bg-[#1e3a5f]/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-stone-900">{eng.name}</span>
                          {formData.construction_engineers.includes(eng.user_id) && (
                            <Check className="h-4 w-4 text-[#1e3a5f]" />
                          )}
                        </div>
                        <span className="text-xs text-stone-500">{eng.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Optional Activities */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-stone-700">Optional Activities</Label>
              <div className="grid grid-cols-2 gap-2">
                {optionalActivities.map(activity => (
                  <div
                    key={activity.key}
                    className="flex items-center space-x-2 p-2 border border-stone-200 rounded-lg"
                  >
                    <Checkbox
                      id={activity.key}
                      checked={formData.optional_activities.includes(activity.key)}
                      onCheckedChange={() => toggleOptionalActivity(activity.key)}
                    />
                    <Label htmlFor={activity.key} className="text-sm text-stone-700 cursor-pointer">
                      {activity.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Admin Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700">Admin Notes</Label>
              <Textarea
                value={formData.admin_notes}
                onChange={(e) => handleChange('admin_notes', e.target.value)}
                placeholder="Any internal notes about this project..."
                className="min-h-[80px] border-stone-200 rounded-lg"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4 border-t border-stone-100">
              <Button
                variant="outline"
                onClick={() => setShowRejectForm(true)}
                className="flex-1 h-11 border-red-200 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <X className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={loading}
                className="flex-1 h-11 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white rounded-lg"
                data-testid="approve-project-button"
              >
                {loading ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Approving...
                  </span>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Approve Project
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectApprovalModal;
